import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { LoadingButton } from '@routes-plan/ui';
import { useT } from '../../i18n';
import StandardModal, { useModalLoading } from '../modales/StandardModal';

type ClientQuoteModalClient = {
  id: number | string;
  idNumber?: string;
  fullName?: string;
  creditLimit?: number | string | null;
  route?: {
    name?: string;
    office?: {
      name?: string;
    };
  };
};

interface ClientQuoteModalProps {
  open: boolean;
  client: ClientQuoteModalClient | null;
  onClose: () => void;
  onSaved?: (nextCreditLimit: number) => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
}

type ClientDetailResponse = {
  client?: ClientQuoteModalClient | null;
};

const toNumericCreditLimit = (value: unknown) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
};

const ClientQuoteModal: React.FC<ClientQuoteModalProps> = ({
  open,
  client,
  onClose,
  onSaved,
  showAlert,
}) => {
  const t = useT();
  const [creditLimit, setCreditLimit] = useState('0');
  const [isSaving, setIsSaving] = useState(false);
  const [resolvedClient, setResolvedClient] = useState<ClientQuoteModalClient | null>(client);
  // Cargando propio del modal: consulta del cliente y guardado.
  const { loading, showLoading, hideLoading } = useModalLoading();

  useEffect(() => {
    setResolvedClient(client);
  }, [client]);

  useEffect(() => {
    if (!open || !client?.id) {
      return;
    }

    let cancelled = false;

    const fetchClientDetail = async () => {
      showLoading();
      try {
        const { data } = await axios.get<ClientDetailResponse>(`/clients/${client.id}`);
        if (!cancelled && data?.client) {
          setResolvedClient(data.client);
        }
      } catch (error) {
        if (!cancelled) {
          setResolvedClient(client);
        }
      } finally {
        hideLoading();
      }
    };

    fetchClientDetail();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCreditLimit(String(toNumericCreditLimit((resolvedClient ?? client)?.creditLimit)));
  }, [client, open, resolvedClient]);

  const effectiveClient = resolvedClient ?? client;

  const clientLabel = useMemo(() => {
    if (!effectiveClient) {
      return '';
    }

    return `${effectiveClient.fullName ?? t('quote.client')}${effectiveClient.idNumber ? ` (${effectiveClient.idNumber})` : ''}`;
  }, [effectiveClient, t]);

  const handleSave = async () => {
    if (!effectiveClient?.id) {
      return;
    }

    const numericCreditLimit = Number(creditLimit);

    if (!Number.isFinite(numericCreditLimit) || numericCreditLimit < 0) {
      showAlert(t('quote.invalid'), 'warning');
      return;
    }

    setIsSaving(true);
    showLoading();
    try {
      await axios.put(`/clients/${effectiveClient.id}/quote`, { creditLimit: numericCreditLimit });
      onSaved?.(numericCreditLimit);
      showAlert(t('quote.saved'), 'success');
      onClose();
    } catch (error: any) {
      showAlert(
        error?.response?.data?.message || t('quote.saveError'),
        'error'
      );
    } finally {
      setIsSaving(false);
      hideLoading();
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={t('quote.title')}
      subtitle={clientLabel || undefined}
      maxWidth={500}
      loading={loading}
      disableClose={isSaving}
      actions={
        <>
          <Button onClick={handleClose} variant="outlined" disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <LoadingButton onClick={handleSave} variant="contained" loading={isSaving}>
            {t('common.save')}
          </LoadingButton>
        </>
      }
    >
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          label={t('common.office')}
          value={effectiveClient?.route?.office?.name || ''}
          InputProps={{ readOnly: true }}
        />
        <TextField
          fullWidth
          size="small"
          label={t('common.route')}
          value={effectiveClient?.route?.name || ''}
          InputProps={{ readOnly: true }}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label={t('quote.limit')}
          value={creditLimit}
          onChange={(event) => setCreditLimit(event.target.value)}
          inputProps={{ min: 0 }}
          sx={{ gridColumn: '1 / -1' }}
        />
      </Box>
    </StandardModal>
  );
};

export default ClientQuoteModal;
